
import React from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CircleOff, Star, CircleDot, Download, BookmarkPlus } from "lucide-react";
import { CrystalBallCalculations } from './types';
import { formatCurrency } from './utils';
import { useToast } from "@/hooks/use-toast";

interface ResultsStepProps {
  calculations: CrystalBallCalculations;
  onStartOver: () => void;
}

const ResultsStep = ({ calculations, onStartOver }: ResultsStepProps) => {
  const { toast } = useToast();
  const { scenarios, averageProjectFee } = calculations;
  const hourlyRate = calculations.dailyRate / 8;

  const handleSave = () => {
    toast({
      title: "Success!",
      description: "Crystal Ball results saved to your profile",
    });
  };

  const handleDownload = () => {
    toast({
      title: "Success!",
      description: "Crystal Ball results downloaded",
    });
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="text-center space-y-4">
        <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-primary">Your Crystal Ball Vision</h3>
        <p className="text-muted-foreground">Based on your desired rate of {formatCurrency(hourlyRate)}/hour, here are three potential scenarios</p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Comfortable Scenario - Featured */}
        <Card className="p-6 bg-gradient-primary text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 p-2">
            <Star className="w-6 h-6 text-yellow-300 animate-pulse" />
          </div>
          <div className="space-y-6">
            <div>
              <h4 className="text-xl font-semibold mb-2">Sweet Spot Scenario</h4>
              <p className="text-white/80">Balanced workload with optimal earning potential</p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-white/80 mb-1">Annual Income</p>
                <p className="text-2xl font-bold">{formatCurrency(scenarios.comfortable.income)}</p>
              </div>
              <div>
                <p className="text-sm text-white/80 mb-1">Monthly Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(scenarios.comfortable.monthlyIncome)}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 bg-white/10 p-4 rounded-lg">
              <div>
                <p className="text-sm text-white/80">Projects/Month</p>
                <p className="font-semibold">{scenarios.comfortable.projectsPerMonth.toFixed(1)}</p>
              </div>
              <div>
                <p className="text-sm text-white/80">Work Hours</p>
                <p className="font-semibold">{Math.round(scenarios.comfortable.workingHours)}</p>
              </div>
              <div>
                <p className="text-sm text-white/80">Utilization</p>
                <p className="font-semibold">{Math.round(scenarios.comfortable.utilizationRate)}%</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Alternative Scenarios */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Minimum Scenario */}
          <Card className="p-6 bg-card hover:bg-card-hover transition-all duration-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="font-semibold text-lg">Comfortable Pace</h4>
                <p className="text-sm text-muted-foreground">Lower intensity, steady income</p>
              </div>
              <CircleOff className="text-primary h-5 w-5" />
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-baseline">
                <span className="text-muted-foreground">Annual Income</span>
                <span className="text-xl font-bold">{formatCurrency(scenarios.minimum.income)}</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Projects/Month</p>
                  <p className="font-semibold">{scenarios.minimum.projectsPerMonth.toFixed(1)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Utilization</p>
                  <p className="font-semibold">{Math.round(scenarios.minimum.utilizationRate)}%</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Maximum Scenario */}
          <Card className="p-6 bg-card hover:bg-card-hover transition-all duration-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="font-semibold text-lg">Maximum Potential</h4>
                <p className="text-sm text-muted-foreground">Peak performance mode</p>
              </div>
              <CircleDot className="text-primary h-5 w-5" />
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-baseline">
                <span className="text-muted-foreground">Annual Income</span>
                <span className="text-xl font-bold">{formatCurrency(scenarios.maximum.income)}</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Projects/Month</p>
                  <p className="font-semibold">{scenarios.maximum.projectsPerMonth.toFixed(1)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Utilization</p>
                  <p className="font-semibold">{Math.round(scenarios.maximum.utilizationRate)}%</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Key Metrics Summary */}
        <Card className="p-6 bg-card border-0">
          <h4 className="text-lg font-semibold mb-4">Key Financial Metrics</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            <div className="space-y-2">
              <p className="text-muted-foreground text-sm">Average Project Value</p>
              <p className="text-xl font-semibold">{formatCurrency(averageProjectFee)}</p>
            </div>
            <div className="space-y-2">
              <p className="text-muted-foreground text-sm">Target Hourly Rate</p>
              <p className="text-xl font-semibold">{formatCurrency(hourlyRate)}</p>
            </div>
            <div className="space-y-2">
              <p className="text-muted-foreground text-sm">Projects per Year</p>
              <p className="text-xl font-semibold">{scenarios.minimum.projects} - {scenarios.maximum.projects}</p>
            </div>
            <div className="space-y-2">
              <p className="text-muted-foreground text-sm">Sweet Spot Utilization</p>
              <p className="text-xl font-semibold">{Math.round(scenarios.comfortable.utilizationRate)}%</p>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={handleSave}
            className="flex-1 sm:flex-initial"
            variant="outline"
          >
            <BookmarkPlus className="mr-2 h-4 w-4" />
            Save to Profile
          </Button>
          <Button
            onClick={handleDownload}
            className="flex-1 sm:flex-initial"
            variant="outline"
          >
            <Download className="mr-2 h-4 w-4" />
            Download Results
          </Button>
          <Button
            onClick={onStartOver}
            className="flex-1 sm:flex-initial"
            variant="ghost"
          >
            Start Over
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ResultsStep;
